import { useApolloClient, useSubscription } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  PREVISIT_INSIGHTS_ADVANCE_MINUTES,
  type PrevisitInsightsStatus,
  canGeneratePrevisitInsights,
} from '@eluve/api-contract';
import {
  AppointmentOutputArtifactTypesLookup,
  type AppointmentStatusEnum,
  WorkerStatusTypesLookup,
} from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';
import type { Spec } from '@eluve/sdui-types';

import { useAppointmentContext } from './appointment.context';

const CACHE_TTL_MS = 1 * 60 * 60 * 1000; // 1 hour

interface CachedArtifact {
  content: Spec;
  id: string;
  status: PrevisitInsightsStatus | undefined;
  /** The patient these insights were generated for (ENG-4601). */
  generatedForPatientId: string | null | undefined;
  fetchedAt: number;
}

const artifactCache = new Map<string, CachedArtifact>();

/**
 * True when a stamped artifact was generated for a different patient than the
 * appointment is now assigned to (reassigned since generation). Only fires when
 * both patients are known and differ; an unstamped (legacy) artifact is treated
 * as valid. The single source of truth for the stale-patient check, applied on
 * every path that surfaces an artifact (cache read, init read, job completion).
 * ENG-4601.
 */
function isStaleForPatient(
  generatedForPatientId: string | null | undefined,
  patientId: string | undefined,
): boolean {
  return Boolean(
    patientId && generatedForPatientId && generatedForPatientId !== patientId,
  );
}

function getCacheKey(
  tenantId: string,
  appointmentId: string,
  patientId?: string,
): string {
  return `${tenantId}:${appointmentId}:${patientId ?? ''}`;
}

function getCachedArtifact(
  tenantId: string,
  appointmentId: string,
  patientId?: string,
): CachedArtifact | null {
  const key = getCacheKey(tenantId, appointmentId, patientId);
  const entry = artifactCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    artifactCache.delete(key);
    return null;
  }
  // Compare-on-read is the authoritative guard: never serve an artifact that
  // was generated for a different patient, even if it landed under this key
  // (e.g. the GET returned the pre-reassignment artifact). ENG-4601.
  if (isStaleForPatient(entry.generatedForPatientId, patientId)) {
    return null;
  }
  return entry;
}

const jobStatusSubscription = graphql(`
  subscription PrevisitInsightsJobStatus(
    $tenantId: uuid!
    $appointmentId: uuid!
    $jobId: uuid!
  ) {
    appointmentLlmJobsByPk(
      tenantId: $tenantId
      appointmentId: $appointmentId
      jobId: $jobId
    ) {
      __typename
      jobId
      jobStatus
    }
  }
`);

const activePrevisitInsightsJobQuery = graphql(`
  query GetActivePrevisitInsightsJob($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentLlmJobs(
      where: {
        tenantId: { _eq: $tenantId }
        appointmentId: { _eq: $appointmentId }
        llmOutputType: { _eq: PREVISIT_INSIGHTS }
        jobStatus: { _in: [NOT_STARTED, IN_PROGRESS] }
      }
      orderBy: { createdAt: DESC }
      limit: 1
    ) {
      __typename
      jobId
      jobStatus
      createdAt
    }
  }
`);

interface UsePrevisitInsightsOptions {
  appointmentId: string;
  /**
   * The patient the appointment is currently assigned to. When it differs from
   * the patient the persisted insights were generated for, the hook regenerates
   * instead of showing stale insights (ENG-4601). When undefined the hook
   * behaves exactly as before (no patient-mismatch handling).
   */
  patientId?: string;
  /**
   * Appointment start (ISO). When set and still beyond the PVI advance window,
   * auto-generation is suppressed and `isAwaitingWindow` is reported; manual
   * generate/regenerate is unaffected. Omitted → legacy always-auto behavior.
   */
  startDate?: string | null;
  /**
   * Appointment status. When set and the status/start_date combination is no
   * longer eligible for PVI (completed, cancelled, stale no-show), generation is
   * blocked and `isGenerationBlocked` is reported; an existing artifact still
   * shows. Omitted → legacy behavior (never blocked).
   */
  status?: AppointmentStatusEnum | null;
  autoTrigger?: boolean;
  skip?: boolean;
}

interface UsePrevisitInsightsResult {
  spec: Spec | null;
  /** Artifact ID for the loaded previsit insights (when spec is present) */
  artifactId: string | null;
  /** Status derived by the backend: 'success', 'no_data', or 'error' */
  status: PrevisitInsightsStatus | undefined;
  /** Auto-generation was suppressed because the visit is beyond the advance window. */
  isAwaitingWindow: boolean;
  /** Generation is not allowed for this appointment's status (completed, cancelled, stale no-show). */
  isGenerationBlocked: boolean;
  isLoading: boolean;
  error: string | null;
  /** Timestamp when loading started, used for accurate progress display */
  loadingStartedAt: number | null;
  generateWithCustomCharts: (
    customCharts: Record<string, unknown>[],
  ) => Promise<void>;
  regenerate: () => Promise<void>;
}

export const usePrevisitInsights = ({
  appointmentId,
  patientId,
  startDate,
  status: appointmentStatus,
  autoTrigger = true,
  skip = false,
}: UsePrevisitInsightsOptions): UsePrevisitInsightsResult => {
  const logger = useNamedLogger('usePrevisitInsights');
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const { tenantId } = useAppointmentContext();

  const [spec, setSpec] = useState<Spec | null>(null);
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [status, setStatus] = useState<PrevisitInsightsStatus | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null);
  const [isAwaitingWindow, setIsAwaitingWindow] = useState(false);

  const hasTriggeredRef = useRef(false);
  const prevPatientIdRef = useRef<string | undefined>(patientId);

  const isBeyondAdvanceWindow = useCallback(() => {
    if (!startDate) return false;
    const start = new Date(startDate).getTime();
    if (Number.isNaN(start)) return false;
    return Date.now() < start - PREVISIT_INSIGHTS_ADVANCE_MINUTES * 60_000;
  }, [startDate]);

  const isGenerationBlocked = !canGeneratePrevisitInsights(
    appointmentStatus,
    startDate,
  );

  const { data: jobData } = useSubscription(jobStatusSubscription, {
    variables: { tenantId, appointmentId, jobId: jobId ?? '' },
    skip: !jobId,
  });

  const jobStatus = jobData?.appointmentLlmJobsByPk?.jobStatus;

  const fetchExistingArtifact = useCallback(async () => {
    const cached = getCachedArtifact(tenantId, appointmentId, patientId);
    if (cached) {
      return {
        content: cached.content,
        id: cached.id,
        status: cached.status,
        generatedForPatientId: cached.generatedForPatientId,
      };
    }

    const response =
      await apiClient.appointmentOutputArtifacts.getAppointmentOutputArtifact({
        params: {
          tenantId,
          appointmentId,
          type: AppointmentOutputArtifactTypesLookup.PREVISIT_INSIGHTS,
        },
      });

    if (response.status === 200) {
      const result = {
        content: response.body.content,
        id: response.body.id,
        status: response.body.status,
        generatedForPatientId: response.body.generatedForPatientId,
      };
      artifactCache.set(getCacheKey(tenantId, appointmentId, patientId), {
        ...result,
        fetchedAt: Date.now(),
      });
      return result;
    }
    return null;
  }, [apiClient, tenantId, appointmentId, patientId]);

  const triggerGeneration = useCallback(
    async (
      customCharts?: Record<string, unknown>[],
      options?: { force?: boolean },
    ) => {
      const force = options?.force ?? false;
      if (hasTriggeredRef.current && !customCharts && !force) return;

      // Never fire for a blocked appointment, even a forced request. Leave
      // hasTriggeredRef untouched so a later status flip re-arms generation.
      if (isGenerationBlocked) {
        setIsLoading(false);
        setLoadingStartedAt(null);
        return;
      }

      hasTriggeredRef.current = true;

      if (force) {
        artifactCache.delete(getCacheKey(tenantId, appointmentId, patientId));
        setArtifactId(null);
      }

      setIsAwaitingWindow(false);
      setIsLoading(true);
      setError(null);
      setSpec(null);
      setStatus(undefined);

      try {
        const response =
          await apiClient.appointmentOutputArtifacts.createAppointmentOutputArtifact(
            {
              params: { tenantId, appointmentId },
              body: {
                type: AppointmentOutputArtifactTypesLookup.PREVISIT_INSIGHTS,
                customCharts,
                force,
              },
            },
          );

        if (response.status === 200) {
          artifactCache.set(getCacheKey(tenantId, appointmentId, patientId), {
            content: response.body.content,
            id: response.body.id,
            status: response.body.status,
            generatedForPatientId: response.body.generatedForPatientId,
            fetchedAt: Date.now(),
          });
          setSpec(response.body.content);
          setArtifactId(response.body.id);
          setStatus(response.body.status);
          setIsLoading(false);
          setLoadingStartedAt(null);
        } else if (response.status === 201 || response.status === 202) {
          logger.info('Previsit insights job in progress', {
            jobId: response.body.jobId,
            status: response.status === 201 ? 'created' : 'in_progress',
            appointmentId,
          });
          const startedAt = new Date(response.body.createdAt).getTime();
          setJobId(response.body.jobId);
          setLoadingStartedAt(startedAt);
        }
      } catch (err) {
        logger.error('Failed to trigger insights generation', {
          error: err,
          appointmentId,
          tenantId,
        });
        setError('Unable to load insights. Please try again later.');
        setIsLoading(false);
        setLoadingStartedAt(null);
      }
    },
    [
      apiClient,
      tenantId,
      appointmentId,
      patientId,
      logger,
      isGenerationBlocked,
    ],
  );

  useEffect(() => {
    if (!jobStatus) return;

    let isCurrent = true;

    if (jobStatus === WorkerStatusTypesLookup.COMPLETED) {
      // The completed job has produced a fresh artifact; drop any cached
      // copy so fetchExistingArtifact pulls the new one from the API.
      artifactCache.delete(getCacheKey(tenantId, appointmentId, patientId));
      fetchExistingArtifact()
        .then((result) => {
          if (!isCurrent) return;
          if (result) {
            // The adopted job was started for a different patient than the
            // appointment is now assigned to (reassigned while it ran). The
            // active-job adoption path matches jobs by appointment only and
            // returns before the read-path stamp guard, so re-check here
            // before surfacing. Force regeneration for the current patient
            // instead of showing the prior patient's insights. ENG-4601.
            if (isStaleForPatient(result.generatedForPatientId, patientId)) {
              setJobId(null);
              triggerGeneration(undefined, { force: true });
              return;
            }
            setSpec(result.content);
            setArtifactId(result.id);
            setStatus(result.status);
          } else {
            setError('Unable to load insights. Please try again later.');
          }
          setIsLoading(false);
          setLoadingStartedAt(null);
          setJobId(null);
        })
        .catch(() => {
          if (!isCurrent) return;
          setError('Unable to load insights. Please try again later.');
          setIsLoading(false);
          setLoadingStartedAt(null);
          setJobId(null);
        });
    } else if (
      jobStatus === WorkerStatusTypesLookup.FAILED ||
      jobStatus === WorkerStatusTypesLookup.FAILED_WORKER_TERMINATED ||
      jobStatus === WorkerStatusTypesLookup.CANCELLED
    ) {
      setError('Unable to load insights. Please try again later.');
      setIsLoading(false);
      setLoadingStartedAt(null);
      setJobId(null);
    }

    return () => {
      isCurrent = false;
    };
  }, [
    jobStatus,
    fetchExistingArtifact,
    triggerGeneration,
    tenantId,
    appointmentId,
    patientId,
  ]);

  // Out-of-band patient switch: the drawer's modal overlay blocks the in-app
  // patient selector while insights are open, but another tab/device/teammate
  // can reassign the appointment and the Apollo subscription pushes the new
  // patientId prop without remounting. Reset so the init effect re-evaluates
  // against the new patient instead of leaving the prior patient's insights on
  // screen. No-op on first render and when patientId is undefined. ENG-4601.
  useEffect(() => {
    if (prevPatientIdRef.current === patientId) return;
    const previousPatientId = prevPatientIdRef.current;
    prevPatientIdRef.current = patientId;

    hasTriggeredRef.current = false;
    artifactCache.delete(
      getCacheKey(tenantId, appointmentId, previousPatientId),
    );
    setSpec(null);
    setArtifactId(null);
    setStatus(undefined);
    setIsAwaitingWindow(false);
  }, [patientId, tenantId, appointmentId]);

  useEffect(() => {
    if (skip) return;

    let cancelled = false;

    const adoptActiveJob = (job: { jobId: string; createdAt: string }) => {
      hasTriggeredRef.current = true;
      setJobId(job.jobId);
      setLoadingStartedAt(new Date(job.createdAt).getTime());
      setIsLoading(true);
      setSpec(null);
      setStatus(undefined);
    };

    const init = async () => {
      // Recomputed below; clear up front so a stale `true` can't leak through an early return.
      setIsAwaitingWindow(false);

      // Check the backend for an active regeneration job started in a prior
      // mount (tab switch, second tab, page refresh). The persisted artifact
      // is the pre-regen copy until the job completes, so finding an active
      // job means we should show loading rather than the stale artifact.
      try {
        const { data } = await apolloClient.query({
          query: activePrevisitInsightsJobQuery,
          variables: { tenantId, appointmentId },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        const activeJob = data?.appointmentLlmJobs?.[0];
        const isDeferredNotStarted =
          activeJob?.jobStatus === WorkerStatusTypesLookup.NOT_STARTED;
        // Adopting a deferred (NOT_STARTED) job pings the create endpoint, which
        // reschedules it to run now — re-arming early generation. Suppress that
        // when charts must be submitted first (autoTrigger off) or still beyond
        // the advance window.
        const suppressDeferredAdoption =
          isDeferredNotStarted &&
          (!autoTrigger || isBeyondAdvanceWindow() || isGenerationBlocked);
        if (activeJob && !suppressDeferredAdoption) {
          adoptActiveJob(activeJob);
          if (isDeferredNotStarted) {
            // Reach the backend's NOT_STARTED reschedule branch. force=true
            // bypasses the create endpoint's existing-artifact short-circuit
            // (which would otherwise return the stale pre-regen artifact and
            // leave the deferred Graphile job sitting at its future runAt);
            // the existing-job check still runs and reuses this jobId via
            // `jobKeyMode: 'replace'`, so no duplicate job is created.
            apiClient.appointmentOutputArtifacts
              .createAppointmentOutputArtifact({
                params: { tenantId, appointmentId },
                body: {
                  type: AppointmentOutputArtifactTypesLookup.PREVISIT_INSIGHTS,
                  force: true,
                },
              })
              .catch((err) => {
                logger.warn(
                  'Failed to ping create endpoint for scheduled previsit insights job',
                  { error: err, appointmentId, tenantId },
                );
              });
          }
          return;
        }
      } catch (err) {
        logger.warn('Failed to query for active previsit insights job', {
          error: err,
          appointmentId,
          tenantId,
        });
        // Fall through to the existing-artifact path.
      }

      if (cancelled || hasTriggeredRef.current) return;

      try {
        const existing = await fetchExistingArtifact();
        if (cancelled || hasTriggeredRef.current) return;

        if (existing) {
          // The persisted artifact was generated for a different patient than
          // the appointment is now assigned to — it has been reassigned since.
          // Regenerate instead of surfacing stale insights. ENG-4601.
          if (isStaleForPatient(existing.generatedForPatientId, patientId)) {
            triggerGeneration(undefined, { force: true });
            return;
          }

          setSpec(existing.content);
          setArtifactId(existing.id);
          setStatus(existing.status);
          setIsLoading(false);
          setLoadingStartedAt(null);
          return;
        }
      } catch {
        // No existing insights
      }

      if (cancelled || hasTriggeredRef.current) return;

      if (!autoTrigger) {
        setIsLoading(false);
        setLoadingStartedAt(null);
        return;
      }

      // Blocked statuses take precedence over the advance window: no auto-gen,
      // just report idle and let isGenerationBlocked drive the UI.
      if (isGenerationBlocked) {
        setIsLoading(false);
        setLoadingStartedAt(null);
        return;
      }

      if (isBeyondAdvanceWindow()) {
        setIsAwaitingWindow(true);
        setIsLoading(false);
        setLoadingStartedAt(null);
        return;
      }

      triggerGeneration();
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [
    skip,
    autoTrigger,
    patientId,
    isBeyondAdvanceWindow,
    isGenerationBlocked,
    triggerGeneration,
    fetchExistingArtifact,
    apolloClient,
    apiClient,
    logger,
    tenantId,
    appointmentId,
  ]);

  const generateWithCustomCharts = useCallback(
    async (customCharts: Record<string, unknown>[]) => {
      await triggerGeneration(customCharts);
    },
    [triggerGeneration],
  );

  const regenerate = useCallback(async () => {
    await triggerGeneration(undefined, { force: true });
  }, [triggerGeneration]);

  return {
    spec,
    artifactId,
    status,
    isAwaitingWindow,
    isGenerationBlocked,
    isLoading,
    error,
    loadingStartedAt,
    generateWithCustomCharts,
    regenerate,
  };
};
